import {
    EXPLORER_TYPE_IDS,
    EXPLORER_VIEW_IDS,
    EXPLORER_VIEWS_BY_TYPE,
    OPEN_SHIPMENT_STATUSES,
    type ExplorerTypeId,
    type ExplorerViewId,
} from "@/constants/explorerViews.constants"
import type { MenuItem } from "@/components/desktop/ui"
import type { WindowState } from "@/components/desktop/types"
import { EXPLORER_COLUMN_GROUPS } from "@/constants/explorerColumns.constants"
import {
    EXPLORER_CONDITION_GROUPS,
    type ExplorerConditionGroup,
} from "@/constants/explorerConditions.constants"
import type { MemoryCategory } from "@/server/workflows/skills/memories/memoryWorkflow.utils"

type ShipmentHealth = "good" | "bad"

const OPEN: ReadonlySet<string> = new Set(OPEN_SHIPMENT_STATUSES)
const TROUBLE: ReadonlySet<string> = new Set(["onHold", "invoiceFlagged"])
const OUT_OF_PLAY: ReadonlySet<string> = new Set(["cancelled", "quoteLost"])

export const shipmentHealth = ({
    status,
    deliveryDueAtMs,
    nowMs,
}: {
    status: string | undefined
    deliveryDueAtMs: number | undefined
    nowMs: number
}): ShipmentHealth | null => {
    if (!status || OUT_OF_PLAY.has(status)) return null
    if (TROUBLE.has(status)) return "bad"
    if (
        OPEN.has(status) &&
        deliveryDueAtMs !== undefined &&
        deliveryDueAtMs < nowMs
    )
        return "bad"
    return "good"
}

export const localViewWindow = (
    now: Date
): { todayStartMs: number; weekEndMs: number; timeZone: string } => {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const daysToSunday = (7 - today.getDay()) % 7
    const weekEnd = new Date(today)
    weekEnd.setDate(today.getDate() + daysToSunday + 1)
    return {
        todayStartMs: today.getTime(),
        weekEndMs: weekEnd.getTime() - 1,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
}

const LEGACY_TYPE_ALIASES: Record<string, ExplorerTypeId> = {
    shipments: "shipments",
    vendors: "vendors",
    quotes: "quotes",
    addresses: "addressBook",
    addressBook: "addressBook",
    salesOrders: "salesOrders",
    products: "products",
    workspace: "workspace",
}

export const EXPLORER_SECTIONS = [
    "skills",
    "memories",
    "rules",
    "knowledgeDocs",
    "templates",
] as const
export type ExplorerSection = (typeof EXPLORER_SECTIONS)[number]

export const EXPLORER_KNOWLEDGE_SECTIONS = [
    "skills",
    "memories",
    "rules",
] as const satisfies readonly ExplorerSection[]
export type ExplorerKnowledgeSection =
    (typeof EXPLORER_KNOWLEDGE_SECTIONS)[number]

export const EXPLORER_OTHER_SECTIONS = [
    "knowledgeDocs",
    "templates",
] as const satisfies readonly ExplorerSection[]

const LEGACY_SECTION_ALIASES: Record<string, ExplorerSection> = {
    knowledgeNotes: "skills",
}

export const isExplorerSection = (value: unknown): value is ExplorerSection =>
    typeof value === "string" &&
    (EXPLORER_SECTIONS as readonly string[]).includes(value)

export const toExplorerSection = (
    value: unknown
): ExplorerSection | undefined => {
    if (isExplorerSection(value)) return value
    return typeof value === "string" ? LEGACY_SECTION_ALIASES[value] : undefined
}

export const MEMORY_CATEGORIES = [
    "correction",
    "preference",
    "contact",
    "distributor",
] as const satisfies readonly MemoryCategory[]

export type ExplorerSelection = {
    typeId: ExplorerTypeId
    viewId?: ExplorerViewId
    savedViewId?: string
    section?: ExplorerSection
    sectionFilter?: string
    isBatchesView?: boolean
}

export const EXPLORER_SECTION_FILTERS: Record<
    ExplorerSection,
    readonly string[]
> = {
    skills: ["custom", "admin"],
    memories: MEMORY_CATEGORIES,
    rules: [],
    knowledgeDocs: ["fromYou", "received"],
    templates: ["pdf", "email"],
}

const SAVED_VIEW_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/

const isViewId = (value: unknown): value is ExplorerViewId =>
    typeof value === "string" &&
    (EXPLORER_VIEW_IDS as readonly string[]).includes(value)

const isTypeId = (value: unknown): value is ExplorerTypeId =>
    typeof value === "string" &&
    (EXPLORER_TYPE_IDS as readonly string[]).includes(value)

const legacyTypeFor = (value: unknown): ExplorerTypeId | undefined =>
    typeof value === "string" && Object.hasOwn(LEGACY_TYPE_ALIASES, value)
        ? LEGACY_TYPE_ALIASES[value]
        : undefined

const isSavedViewId = (value: unknown): value is string =>
    typeof value === "string" && SAVED_VIEW_ID_PATTERN.test(value)

export const initialExplorerSelection = (
    data: Record<string, unknown> | undefined
): ExplorerSelection => {
    const typeId = data?.typeId
    const viewId =
        data?.isMineOnly === true && !data?.viewId && !data?.savedViewId
            ? "shipmentsMine"
            : data?.viewId
    const section = toExplorerSection(data?.section)
    if (section) {
        const sectionFilter = data?.sectionFilter
        return {
            typeId: isTypeId(typeId) ? typeId : "shipments",
            section,
            ...(typeof sectionFilter === "string" &&
            EXPLORER_SECTION_FILTERS[section].includes(sectionFilter)
                ? { sectionFilter }
                : {}),
        }
    }
    if (typeId === "shipments" && data?.isBatchesView === true) {
        return { typeId, isBatchesView: true }
    }
    if (isTypeId(typeId)) {
        return {
            typeId,
            ...(isViewId(viewId) &&
            EXPLORER_VIEWS_BY_TYPE[typeId].includes(viewId)
                ? { viewId }
                : {}),
            ...(isSavedViewId(data?.savedViewId)
                ? { savedViewId: data.savedViewId }
                : {}),
        }
    }
    for (const legacy of [data?.expandedPath, data?.activeTab]) {
        const legacyTypeId = legacyTypeFor(legacy)
        if (legacyTypeId) return { typeId: legacyTypeId }
    }
    return { typeId: "shipments", viewId: "shipmentsAllOpen" }
}

export const hasExplorerTarget = (
    data: Record<string, unknown> | undefined
): boolean =>
    toExplorerSection(data?.section) !== undefined ||
    isTypeId(data?.typeId) ||
    isSavedViewId(data?.savedViewId) ||
    data?.isMineOnly === true ||
    legacyTypeFor(data?.expandedPath) !== undefined ||
    legacyTypeFor(data?.activeTab) !== undefined

export const explorerSelectionToWindowData = (
    selection: ExplorerSelection
): Record<string, unknown> => ({
    typeId: selection.typeId,
    viewId: selection.viewId,
    savedViewId: selection.savedViewId,
    section: selection.section,
    sectionFilter: selection.sectionFilter,
    isMineOnly: undefined,
    isBatchesView: selection.isBatchesView,
    expandedPath: undefined,
    activeTab: undefined,
})

export const openFocusedRecordId = ({
    windows,
    explorerWindowId,
    recordId,
}: {
    windows: ReadonlyArray<Pick<WindowState, "type" | "data">>
    explorerWindowId: string
    recordId: string | undefined
}): string | undefined =>
    recordId !== undefined &&
    windows.some(
        w =>
            w.type === "document" &&
            w.data?.documentId === recordId &&
            w.data?.originWindowId === explorerWindowId
    )
        ? recordId
        : undefined

export const explorerSelectionKey = (selection: ExplorerSelection): string => {
    const normalized = initialExplorerSelection(
        explorerSelectionToWindowData(selection)
    )
    return JSON.stringify([
        normalized.typeId,
        normalized.viewId,
        normalized.savedViewId,
        normalized.section,
        normalized.sectionFilter,
        normalized.isBatchesView,
    ])
}

const EXPLORER_URL_PARAMS = {
    typeId: "type",
    viewId: "view",
    savedViewId: "savedView",
    isMineOnly: "mine",
    isBatchesView: "batches",
    section: "knowledge",
    sectionFilter: "filter",
} as const

export const explorerSearchParamsFromWindowData = (
    data: Record<string, unknown> | undefined
): [string, string][] => {
    const selection = initialExplorerSelection(data)
    if (selection.section) {
        const sectionEntries: [string, string][] = [
            [EXPLORER_URL_PARAMS.section, selection.section],
        ]
        if (selection.sectionFilter) {
            sectionEntries.push([
                EXPLORER_URL_PARAMS.sectionFilter,
                selection.sectionFilter,
            ])
        }
        return sectionEntries
    }
    const entries: [string, string][] = [
        [EXPLORER_URL_PARAMS.typeId, selection.typeId],
    ]
    if (selection.isBatchesView) {
        entries.push([EXPLORER_URL_PARAMS.isBatchesView, "1"])
    }
    if (selection.viewId) {
        entries.push([EXPLORER_URL_PARAMS.viewId, selection.viewId])
    }
    if (selection.savedViewId) {
        entries.push([EXPLORER_URL_PARAMS.savedViewId, selection.savedViewId])
    }
    return entries
}

export const explorerWindowDataFromSearchParams = (
    searchParams: URLSearchParams
): Record<string, unknown> | undefined => {
    const section = searchParams.get(EXPLORER_URL_PARAMS.section)
    const typeId = searchParams.get(EXPLORER_URL_PARAMS.typeId)
    if (toExplorerSection(section) || isTypeId(typeId)) {
        return explorerSelectionToWindowData(
            initialExplorerSelection({
                section,
                sectionFilter: searchParams.get(
                    EXPLORER_URL_PARAMS.sectionFilter
                ),
                typeId,
                viewId: searchParams.get(EXPLORER_URL_PARAMS.viewId),
                savedViewId: searchParams.get(EXPLORER_URL_PARAMS.savedViewId),
                isMineOnly:
                    searchParams.get(EXPLORER_URL_PARAMS.isMineOnly) === "1",
                isBatchesView:
                    searchParams.get(EXPLORER_URL_PARAMS.isBatchesView) === "1",
            })
        )
    }
    const path = searchParams.get("path")
    if (!legacyTypeFor(path)) return undefined
    return explorerSelectionToWindowData(
        initialExplorerSelection({ expandedPath: path })
    )
}

export type ExplorerBatchRow<TShipment> =
    | {
          kind: "batch"
          id: string
          referenceNumber: string | null
          shipmentCount: number
          isExpanded: boolean
      }
    | { kind: "shipment"; id: string; batchId: string; shipment: TShipment }

export const flattenExplorerBatches = <TShipment extends { id: string }>({
    batches,
    shipments,
    expandedBatchIds,
}: {
    batches: readonly {
        id: string
        referenceNumber: string | null
        childIds: readonly string[]
    }[]
    shipments: readonly TShipment[]
    expandedBatchIds: ReadonlySet<string>
}): ExplorerBatchRow<TShipment>[] => {
    const shipmentById = new Map(shipments.map(row => [row.id, row]))
    return batches.flatMap(batch => {
        const children = batch.childIds.flatMap(childId => {
            const shipment = shipmentById.get(childId)
            return shipment ? [shipment] : []
        })
        const isExpanded = expandedBatchIds.has(batch.id)
        const batchRow: ExplorerBatchRow<TShipment> = {
            kind: "batch",
            id: batch.id,
            referenceNumber: batch.referenceNumber,
            shipmentCount: children.length,
            isExpanded,
        }
        if (!isExpanded) return [batchRow]
        return [
            batchRow,
            ...children.map((shipment): ExplorerBatchRow<TShipment> => ({
                kind: "shipment",
                id: shipment.id,
                batchId: batch.id,
                shipment,
            })),
        ]
    })
}

export const userDisplayName = ({
    id,
    firstName,
    lastName,
    email,
}: {
    id: string
    firstName?: string | null
    lastName?: string | null
    email?: string | null
}): string =>
    [firstName, lastName]
        .map(part => part?.trim())
        .filter(Boolean)
        .join(" ") ||
    email?.trim() ||
    id

interface ExplorerValueOption {
    value: string
    count: number | undefined
}

export const buildAssigneeOptions = ({
    matchedUserIds,
    facet,
    picked,
    search,
    nameOf,
}: {
    matchedUserIds: readonly string[]
    facet: readonly { value: string; count: number }[]
    picked: readonly string[]
    search: string
    nameOf: (userId: string) => string
}): ExplorerValueOption[] => {
    const countById = new Map(facet.map(option => [option.value, option.count]))
    const needle = search.trim().toLowerCase()
    const matchedIds = new Set(matchedUserIds)
    const facetMatches = facet
        .map(option => option.value)
        .filter(
            id =>
                matchedIds.has(id) ||
                !needle ||
                nameOf(id).toLowerCase().includes(needle)
        )
    const matching = [...new Set([...matchedUserIds, ...facetMatches])]
        .map(id => ({ value: id, count: countById.get(id) }))
        .sort(
            (a, b) =>
                (b.count ?? 0) - (a.count ?? 0) ||
                nameOf(a.value).localeCompare(nameOf(b.value))
        )
    const missing = picked
        .filter(id => !matching.some(option => option.value === id))
        .map(id => ({ value: id, count: undefined }))
    return [...missing, ...matching]
}

interface ExplorerColumnGroup<T extends { key: string }> {
    group: ExplorerConditionGroup | null
    columns: T[]
}

export const groupExplorerColumns = <T extends { key: string }>({
    typeId,
    columns,
}: {
    typeId: ExplorerTypeId
    columns: readonly T[]
}): ExplorerColumnGroup<T>[] => {
    const groupKeys = EXPLORER_COLUMN_GROUPS[typeId] ?? {}
    const byKey = new Map(columns.map(column => [column.key, column]))
    const claimed = new Set<string>()
    const groups: ExplorerColumnGroup<T>[] = EXPLORER_CONDITION_GROUPS.map(
        group => ({
            group,
            columns: (groupKeys[group] ?? []).flatMap(key => {
                const column = byKey.get(key)
                if (!column) return []
                claimed.add(key)
                return [column]
            }),
        })
    )
    groups.push({
        group: null,
        columns: columns.filter(column => !claimed.has(column.key)),
    })
    return groups.filter(group => group.columns.length > 0)
}

export function pickerMenuItems<T extends string>({
    options,
    value,
    onPick,
}: {
    options: { value: T; label: string; section?: string }[]
    value?: T
    onPick: (value: T) => void
}): MenuItem[] {
    return options.map((option): MenuItem => ({
        label: option.label,
        section: option.section,
        isSelected: value === undefined ? undefined : option.value === value,
        onSelect: () => onPick(option.value),
    }))
}
